import { TranslatePipe } from "@ngx-translate/core";
import { Apollo } from "apollo-angular";
import { firstValueFrom } from "rxjs";

import { ChangeDetectionStrategy, Component, computed, inject, input, resource, signal } from "@angular/core";
import { Sort } from "@angular/material/sort";

import { AirtScanConnection, AirtSort, SearchFilter } from "../../../../apis/networkdashboard/graphql/graphql";
import { AIRT_SCAN_QUERY } from "../../../../apis/networkdashboard/static/graphql-queries/airtscan";
import { CertChartComponent } from "../../../../components/cert/cert-chart/cert-chart";
import { CertFilter } from "../../../../components/cert/cert-filter/cert-filter";
import { CertTable } from "../../../../components/cert/cert-table/cert-table";
import { AnalyticsService } from "../../../../services/analytics.service";
import {
  AnalyticsAction,
  AnalyticsModule,
  CertLogInteraction,
  CertLogView,
} from "../../../../services/analytics.types";

const EMPTY_PAGE_INFO = {
  filterFields: [],
  hasNextPage: false,
  hasPreviousPage: false,
  sortFields: [],
  endCursor: 0,
  startCursor: 0,
  totalItems: 0,
};

@Component({
  selector: "scan-tab",
  templateUrl: "scan-tab.html",
  styleUrl: "../../cert.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CertChartComponent, CertFilter, CertTable, TranslatePipe],
})
export class ScanTab {
  private apollo = inject(Apollo);
  private analytics = inject(AnalyticsService);

  public prefixes = input.required<string[]>();
  private filterInitialised = false;

  private trackInspection(interaction: CertLogInteraction) {
    this.analytics.trackFeature(
      AnalyticsModule.Cert,
      AnalyticsAction.InspectCertLog,
      `${CertLogView.SecurityScan}:${interaction}`,
    );
  }
  protected airtScanResults = resource({
    params: () => ({
      filter: this.searchFilter(),
      paginationAfter: this.paginationAfter(),
      sort: this.querySort(),
    }),
    loader: async ({ params }) => {
      const result = await this.fetchAirtScanInformation(params);
      return result.data?.airtScans ?? { edges: [], pageInfo: EMPTY_PAGE_INFO };
    },
    defaultValue: { edges: [], pageInfo: EMPTY_PAGE_INFO },
  });

  protected airtScans = computed(() => {
    if (!this.airtScanResults.hasValue()) {
      return [];
    }
    return this.airtScanResults.value().edges;
  });
  protected pageInfo = computed(() =>
    this.airtScanResults.hasValue() ? (this.airtScanResults.value().pageInfo ?? EMPTY_PAGE_INFO) : EMPTY_PAGE_INFO,
  );
  private querySort = computed(() => ({
    order: [{ [this.sortInfo().active || "ip"]: this.sortInfo().direction.toUpperCase() || "ASC" }],
  }));

  protected searchFilter = signal<SearchFilter>({});
  private paginationAfter = signal(0);
  private sortInfo = signal<Sort>({
    active: "ip",
    direction: "asc",
  });

  private fetchAirtScanInformation(args: { filter: SearchFilter; paginationAfter: number; sort: AirtSort }) {
    return firstValueFrom(
      this.apollo.query<{ airtScans: AirtScanConnection }>({
        query: AIRT_SCAN_QUERY,
        fetchPolicy: "network-only",
        errorPolicy: "all",
        variables: {
          filter: args.filter,
          first: 20,
          after: args.paginationAfter,
          sort: args.sort,
        },
      }),
    );
  }

  protected onFiltersChanged(event: SearchFilter) {
    this.searchFilter.set(event);
    if (this.filterInitialised) {
      // CertFilterDns emits on init via an effect; skip the first emission
      // so only deliberate user filtering is tracked
      this.trackInspection(CertLogInteraction.Filter);
    }
    this.filterInitialised = true;
  }

  protected onPaginationChanged(event: number) {
    this.paginationAfter.set(event > -1 ? event : 0);
    this.trackInspection(CertLogInteraction.Paginate);
  }

  protected onSortingChanged(event: Sort) {
    this.sortInfo.set(event);
    this.trackInspection(CertLogInteraction.Sort);
  }
}
